import {AfterViewChecked, ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, Signal} from '@angular/core';
import {AudioRecorderService} from "@store/file-upload/audio-recorder.service";
import {MatButtonModule} from "@angular/material/button";
import {MatIconModule} from "@angular/material/icon";
import {AsyncPipe, NgForOf, NgIf} from "@angular/common";
import {BehaviorSubject, EMPTY, of, switchMap} from "rxjs";
import {TranslocoModule} from "@ngneat/transloco";
import {ImageUpload} from "../../../../../../models/questions/ImageUpload";
import {AudioUploadService, IAudioUpload} from "@store/file-upload/audio-upload.service";
import {SurveyViewerService} from "../../../services/survey-viewer.service";
import {AbstractControl} from "@angular/forms";
import {IQuestionComponent} from "../../../../../../interfaces/IQuestionComponent";
import {MatTooltipModule} from "@angular/material/tooltip";

@Component({
  selector: 'app-audio-recorder',
  standalone: true,
  imports: [
    MatButtonModule,
    MatIconModule,
    NgIf,
    AsyncPipe,
    TranslocoModule,
    MatTooltipModule,
    NgForOf
  ],
  templateUrl: './audio-recorder.component.html',
  styleUrl: './audio-recorder.component.scss'
})
export class AudioRecorderComponent implements IQuestionComponent, AfterViewChecked {
  errorMessages = {
    noRecordingValidator: 'Champ obligatoire',
    noTextCanBeHeard: 'Échec de l\'enregistrement, veuillez réessayer'
  }

  isRecording = new BehaviorSubject<boolean>(false);
  audioBlob?: Blob;
  audioBlobUrl = new BehaviorSubject<string>(undefined);
  @Input() isSmallScreen: boolean;
  @Input() activeLang: string;
  @Input() question: ImageUpload;

  questionFormControl: AbstractControl<any, any>;
  audioFormControl: AbstractControl<any, any>;
  commentsFormControl: AbstractControl<any, any>;

  audioUrl = '';

  hasError = new BehaviorSubject<Array<string>>([]);

  constructor(
    private recorder: AudioRecorderService,
    public audioUploadService: AudioUploadService,
    public surveyViewerService: SurveyViewerService,
    private cdr: ChangeDetectorRef,
  ) {
  }

  ngAfterViewChecked() {
    this.audioUploadService.uploadedAudio$[this.question.key] ??= new BehaviorSubject<IAudioUpload>(null);
    this.questionFormControl = this.surveyViewerService.activeSurvey$.value.form.get(this.question.key);
    this.audioFormControl = this.questionFormControl.get(this.question.rowsOrder[0]).get(this.question.audioControlName).get(this.question.statementsOrder[0]);
    this.commentsFormControl = this.questionFormControl.get(this.question.rowsOrder[0]).get(this.question.commentsControlName).get(this.question.statementsOrder[0]);
    if(this.audioFormControl?.value){
      this.audioUrl = this.audioFormControl.value;
      this.audioBlobUrl.next(this.audioFormControl.value)
      this.cdr.detectChanges();
    }
    this.audioUploadService.uploadedAudio$[this.question.key]
      .pipe(switchMap((d) => d && (d.url !== this.audioUrl) ? of(d) : EMPTY))
      .subscribe((audioData) => {
        if (!audioData.transcription?.length) {
          this.hasError.next([this.errorMessages.noTextCanBeHeard]);
          this.audioBlob = undefined;
          this.audioBlobUrl.next(undefined);
          this.audioUploadService.uploadedAudio$[this.question.key].next(null);
        }else{
          this.audioUrl = audioData?.url ?? '';
          this.audioFormControl.setValue(this.audioUrl);
          this.commentsFormControl.setValue(audioData.transcription)
        }
      });
    this.questionFormControl.get(this.question.rowsOrder[0]).get(this.question.audioControlName)
      .get(this.question.statementsOrder[0]).statusChanges.subscribe(value => {
      if (value === 'VALID') {
        this.surveyViewerService.activeSurvey$.value.form.get(this.question.key).markAsTouched()
      } else {
        this.hasError.next(Object.entries(this.audioFormControl.errors).map(([key, value]) => {
          if (value) {
            return this.errorMessages[key];
          }
        }).filter(Boolean));
      }
    })
    this.audioFormControl.markAsTouched();
    this.surveyViewerService.activeSurvey$.subscribe(survey => {this.hasError.next(this.hasError.value || [])});
  }

  async start() {
    this.isRecording.next(true);
    await this.recorder.startRecording(this.question.key);
    this.audioFormControl.markAsUntouched();
  }

  async stop() {
    this.isRecording.next(false);
    this.audioBlob = await this.recorder.stopRecording(this.question.key);
    this.audioBlobUrl.next(URL.createObjectURL(this.audioBlob));
    this.upload();
  }

  eraseAudio() {
    this.audioBlob = undefined;
    this.audioBlobUrl.next(undefined);
    this.audioUploadService.uploadedAudio$[this.question.key]?.next(null);
    this.audioUploadService.isUploading$[this.question.key]?.next(false);
    this.audioFormControl.setValue(null);
    this.commentsFormControl.setValue('');
  }

  upload() {
    if (!this.audioBlob) return;
    this.hasError.next([]);
    this.audioUploadService.uploadAudioFile(this.audioBlob, this.question.key, this.surveyViewerService.activeSurvey$.value.id);
  }

}
